'use client';

import { Season } from '@prisma/client';
import { usePathname } from 'next/navigation';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import useSWR from 'swr';

type SeasonThemeContextType = {
    colorScheme: string;
    seasonShortName: string | null;
    isOG: boolean;
    logoPath: string | null;
};

const SeasonThemeContext = createContext<SeasonThemeContextType>({
    colorScheme: 'default',
    seasonShortName: null,
    isOG: false,
    logoPath: null,
});

function extractSeasonFromPath(pathname: string): string | null {
    // Match /seasons/shortName
    const seasonMatch = pathname.match(/^\/seasons\/([^\/]+)/);
    if (seasonMatch) return decodeURIComponent(seasonMatch[1]);

    // Match /creator/name/shortName
    const creatorMatch = pathname.match(/^\/creator\/[^\/]+\/([^\/]+)/);
    if (creatorMatch) return decodeURIComponent(creatorMatch[1]);

    return null;
}

export function SeasonThemeProvider({ children }: { children: ReactNode }) {
    const pathname = usePathname();
    const { data: seasons } = useSWR<Season[]>('/api/data/seasonsList');

    const [resolvedTheme, setResolvedTheme] = useState<SeasonThemeContextType>({
        colorScheme: 'default',
        seasonShortName: null,
        isOG: false,
        logoPath: null,
    });

    useEffect(() => {
        // Wait for pathname (avoids flash during SSR hydration)
        if (!pathname) return;

        // Admin pages always use default theme
        if (pathname.startsWith('/admin')) {
            setResolvedTheme({
                colorScheme: 'default',
                seasonShortName: null,
                isOG: false,
                logoPath: null,
            });
            document.documentElement.dataset.seasonTheme = 'default';
            document.documentElement.removeAttribute('data-season-og');
            return;
        }

        const seasonShortName = extractSeasonFromPath(pathname);

        if (seasonShortName && seasons) {
            // Season-specific page — find the matching season
            const season = seasons.find(s => s.shortName === seasonShortName);
            if (season) {
                const colorScheme = season.colorScheme || 'default';
                setResolvedTheme({
                    colorScheme,
                    seasonShortName,
                    isOG: season.isOG,
                    logoPath: season.logoPath,
                });
                document.documentElement.dataset.seasonTheme = colorScheme;
                if (season.isOG) {
                    document.documentElement.setAttribute('data-season-og', 'true');
                } else {
                    document.documentElement.removeAttribute('data-season-og');
                }
            } else {
                // Season not found in data — keep default
                setResolvedTheme(prev => ({ ...prev, seasonShortName }));
                document.documentElement.dataset.seasonTheme = 'default';
                document.documentElement.removeAttribute('data-season-og');
            }
            return;
        }

        if (!seasonShortName) {
            // General page (homepage, /join, /players, etc.)
            // Try meta tag first (SSR-provided theme hint), then fall back to latest non-OG season
            const metaTheme = document.querySelector<HTMLMetaElement>(
                'meta[name="x-current-season-theme"]',
            )?.content;

            if (seasons && seasons.length > 0) {
                const newSeasons = seasons
                    .filter(s => !s.isOG && s.colorScheme !== null)
                    .sort((a, b) => b.id - a.id);

                if (newSeasons.length > 0) {
                    const latest = newSeasons[0];
                    const colorScheme = latest.colorScheme || 'default';
                    setResolvedTheme({
                        colorScheme,
                        seasonShortName: null,
                        isOG: false,
                        logoPath: latest.logoPath,
                    });
                    document.documentElement.dataset.seasonTheme = colorScheme;
                    document.documentElement.removeAttribute('data-season-og');
                } else {
                    // All seasons are OG — use default
                    setResolvedTheme({
                        colorScheme: 'default',
                        seasonShortName: null,
                        isOG: false,
                        logoPath: null,
                    });
                    document.documentElement.dataset.seasonTheme = 'default';
                    document.documentElement.removeAttribute('data-season-og');
                }
            } else if (metaTheme) {
                // SWR hasn't loaded yet but we have a meta hint (homepage SSR)
                setResolvedTheme({
                    colorScheme: metaTheme,
                    seasonShortName: null,
                    isOG: false,
                    logoPath: null,
                });
                document.documentElement.dataset.seasonTheme = metaTheme;
                document.documentElement.removeAttribute('data-season-og');
            }
            // else: SWR loading, no meta — keep current state (default from HTML attribute)
        }
    }, [pathname, seasons]);

    return (
        <SeasonThemeContext.Provider value={resolvedTheme}>
            {children}
        </SeasonThemeContext.Provider>
    );
}

export function useSeasonTheme() {
    return useContext(SeasonThemeContext);
}
